import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import LandingNav from '../components/shared/LandingNav';
import SiteFooter from '../components/shared/SiteFooter';
import StickyCTA from '../components/shared/StickyCTA';
import LandingBackdrop from '../components/landing/LandingBackdrop';
import Seo from '../components/Seo';
import { FLASH_SALE_CAMPAIGN } from '../data/flashSale/campaignConfig';
import {
  buildExpiredFallbackUrl,
  buildRegisterUrl,
  consumeCheckoutIntent,
  getOfferSummary,
  isCampaignPurchasable,
  persistCampaignAttribution,
} from '../data/flashSale/campaignHelpers';
import { useFlashSaleCheckout } from '../components/flashSale/useFlashSaleCheckout';
import {
  AnnouncementBar,
  DifferentiationSection,
  FaqSection,
  FeaturesSection,
  GuaranteeSection,
  HeroSection,
  HowItWorksSection,
  OfferSection,
  ProblemSection,
  VslSection,
} from '../components/flashSale/FlashSaleSections';
import analyticsService from '../services/analyticsService';
import { isPrerendering } from '../utils/prerendering';
import './Landing.css';
import './HomeDesk.css';
import './FlashSale.css';

/**
 * Public flash-sale landing page — Precision Desk campaign surface.
 * Configure via src/data/flashSale/campaignConfig.js + REACT_APP_FLASH_SALE_* env.
 */
const FlashSale = () => {
  const campaign = FLASH_SALE_CAMPAIGN;
  const [searchParams] = useSearchParams();
  const [tick, setTick] = useState(0);
  const offerRef = useRef(null);
  const pageViewSent = useRef(false);
  const { startCheckout, busy } = useFlashSaleCheckout(campaign);

  const offer = useMemo(
    () => getOfferSummary(campaign, Date.now()),
    // tick forces recompute after countdown expiry
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaign, tick]
  );

  const handleExpire = useCallback(() => {
    setTick((n) => n + 1);
    analyticsService.trackTradingEvent('flash_sale_campaign_expired', {
      campaign_id: campaign.id,
      source: 'countdown',
    });
  }, [campaign.id]);

  useEffect(() => {
    if (isPrerendering()) return;
    window.scrollTo(0, 0);
    persistCampaignAttribution(campaign);

    if (!pageViewSent.current) {
      pageViewSent.current = true;
      analyticsService.trackTradingEvent(campaign.analytics.pageEvent, {
        campaign_id: campaign.id,
        campaign_status: getOfferSummary(campaign).status,
        utm_source: searchParams.get('utm_source'),
        utm_medium: searchParams.get('utm_medium'),
        utm_campaign: searchParams.get('utm_campaign'),
        utm_content: searchParams.get('utm_content'),
        utm_term: searchParams.get('utm_term'),
      });
    }
  }, [campaign, searchParams]);

  // Auto-start checkout after register → redirect=/flash-sale?checkout=1
  const autoCheckoutStarted = useRef(false);
  useEffect(() => {
    if (isPrerendering() || autoCheckoutStarted.current) return;
    const wantsCheckout =
      searchParams.get('checkout') === '1' || consumeCheckoutIntent();
    if (!wantsCheckout) return;
    if (!isCampaignPurchasable(campaign)) return;
    autoCheckoutStarted.current = true;
    startCheckout({ source: 'post_register_redirect' });
  }, [campaign, searchParams, startCheckout]);

  const onPrimaryCta = useCallback(() => {
    analyticsService.trackTradingEvent('flash_sale_hero_cta_click', {
      campaign_id: campaign.id,
      status: offer.status,
    });
    if (offer.purchasable) {
      startCheckout({ source: 'hero' });
    } else {
      window.location.assign(buildExpiredFallbackUrl(campaign));
    }
  }, [campaign, offer.purchasable, offer.status, startCheckout]);

  const onSecondaryCta = useCallback(() => {
    const el = document.getElementById(campaign.cta.secondaryScrollTarget);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [campaign.cta.secondaryScrollTarget]);

  const onPricingCta = useCallback(() => {
    if (offer.purchasable) {
      startCheckout({ source: 'pricing_section' });
    } else {
      window.location.assign(buildExpiredFallbackUrl(campaign));
    }
  }, [campaign, offer.purchasable, startCheckout]);

  const stickyLink = offer.purchasable
    ? buildRegisterUrl(campaign)
    : buildExpiredFallbackUrl(campaign);

  const seoDescription = offer.purchasable
    ? `Limited-time PrecisionTrader offer: ${offer.plan.displayName} for ${offer.promoDisplay}${offer.suffix}. Rule enforcement, risk tools, and trade journaling.`
    : 'PrecisionTrader flash sale landing — rule enforcement, risk management, and trade journaling for discretionary traders.';

  return (
    <>
      <Seo
        title="Flash Sale | PrecisionTrader"
        description={seoDescription}
        canonicalPath="/flash-sale"
        image={`${campaign.media.heroImage}`}
      />
      <div className="landing-page home-desk flash-sale-page">
        <LandingBackdrop />
        <div className="landing-content-shell">
          <AnnouncementBar campaign={campaign} offer={offer} />
          <LandingNav />

          <main>
            <HeroSection
              campaign={campaign}
              offer={offer}
              onPrimaryCta={onPrimaryCta}
              onSecondaryCta={onSecondaryCta}
              busy={busy}
            />
            <ProblemSection />
          <VslSection
            campaign={campaign}
            offer={offer}
            onCta={onPricingCta}
            busy={busy}
          />
          <HowItWorksSection />
          <FeaturesSection />
          <DifferentiationSection />
          <OfferSection
            campaign={campaign}
            offer={offer}
            onCta={onPricingCta}
            busy={busy}
            onExpire={handleExpire}
            offerSectionRef={offerRef}
          />
          <GuaranteeSection campaign={campaign} />
          <FaqSection campaign={campaign} offer={offer} />
        </main>

        <SiteFooter />
        </div>
        <StickyCTA
          ctaText={
            offer.purchasable && offer.promoDisplay
              ? `Get Pro · ${offer.promoDisplay}${offer.suffix}`
              : campaign.copy.expiredCta
          }
          ctaLink={stickyLink}
          copy={
            offer.purchasable
              ? `Flash sale · ${offer.promoDisplay}${offer.suffix}`
              : 'Standard pricing on the pricing page.'
          }
          onCtaClick={() => {
            analyticsService.trackTradingEvent('flash_sale_sticky_cta_click', {
              campaign_id: campaign.id,
            });
          }}
        />
      </div>
    </>
  );
};

export default FlashSale;
